import React from 'react'
import isNil from 'lodash/isNil'
import dayjs from 'dayjs'
import {
  InvoiceDeliveryItemDto,
  InvoiceDeliveryStatus,
  InvoiceMetadata,
  InvoiceTemplate,
} from '../../api/dto/invoice-delivery.dto'

export const STATUS_LABELS: Record<InvoiceDeliveryStatus, string> = {
  [InvoiceDeliveryStatus.Draft]: 'Draft',
  [InvoiceDeliveryStatus.Sending]: 'Sending',
  [InvoiceDeliveryStatus.Delivered]: 'Delivered',
  [InvoiceDeliveryStatus.Failed]: 'Failed',
}

export const PARTIALLY_DELIVERED_LABEL = 'Partially Delivered'

/**
 * True when a delivery reached some recipients but not all.
 *
 * Derived here rather than served by the API: the backend rolls per-recipient
 * outcomes up all-or-nothing, so one bounce marks the whole delivery `failed`
 * even when most recipients received the invoice. Keep this the only place the
 * rule lives — if a `partially_delivered` status is added server-side later,
 * this is the single call site to retire.
 */
export function isPartiallyDelivered(
  delivery: Pick<InvoiceDeliveryItemDto, 'status' | 'emailStats'>
): boolean {
  if (delivery.status !== InvoiceDeliveryStatus.Failed) return false
  const stats = delivery.emailStats
  if (isNil(stats)) return false
  return stats.delivered > 0 && stats.delivered < stats.total
}

/**
 * Status label that distinguishes a partial delivery from a total failure.
 * Falls back to the raw status for any value not in STATUS_LABELS.
 */
export function resolveStatusLabel(
  delivery: Pick<InvoiceDeliveryItemDto, 'status' | 'emailStats'>
): string {
  if (isPartiallyDelivered(delivery)) return PARTIALLY_DELIVERED_LABEL
  return STATUS_LABELS[delivery.status] ?? delivery.status
}

/**
 * Key for StatusBadge / STATUS_COLORS. Returns the synthetic
 * `partially_delivered` variant for partial deliveries so they render in
 * warning orange instead of failure red.
 */
export function resolveStatusBadgeKey(
  delivery: Pick<InvoiceDeliveryItemDto, 'status' | 'emailStats'>
): string {
  return isPartiallyDelivered(delivery)
    ? 'partially_delivered'
    : delivery.status
}

/**
 * Whether a delivery has been dispatched at least once, and so a send action
 * on it would be a re-send rather than a first send.
 */
export function hasBeenSent(
  delivery: Pick<InvoiceDeliveryItemDto, 'status'>
): boolean {
  return delivery.status !== InvoiceDeliveryStatus.Draft
}

/**
 * Whether a send or resend can be initiated for this delivery.
 *
 * Single source of truth for both the row action and bulk selection — keeping
 * these as separate hand-written predicates previously let a row be
 * checkbox-selectable while being silently dropped from the submitted ids.
 *
 * `Delivered` rows are eligible: bulk send resolves per delivery whether
 * anything is outstanding and skips rows with nothing to do, so selecting one
 * is safe rather than an error.
 */
export function canSendDelivery(delivery: InvoiceDeliveryItemDto): boolean {
  return (
    !!delivery.clientId &&
    delivery.isLatest &&
    !delivery.isArchived &&
    delivery.hasBillingProfile !== false &&
    delivery.status !== InvoiceDeliveryStatus.Sending
  )
}

/**
 * Whether the delivery has an uploaded file. Reads `s3Key` rather than
 * `downloadUrl`: EPS leaves the URL out for users who may not download
 * invoices (EMS-10939), and they still select rows to send.
 */
export const hasInvoiceFile = (
  delivery: Pick<InvoiceDeliveryItemDto, 's3Key'>
): boolean => Boolean(delivery.s3Key)

/**
 * Whether the send action should be rendered at all for this delivery.
 *
 * A historical or archived row is not a thing an operator can ever send, so it
 * shows no send control — not even a disabled one. Every other blocked case
 * still renders disabled with a reason, because those are states the row can
 * recover from (match it, add recipients, wait for the send to settle).
 */
export function shouldShowSendAction(
  delivery: InvoiceDeliveryItemDto
): boolean {
  return delivery.isLatest && !delivery.isArchived
}

/** Mirrors EPS's send check: the OE1 email states both amounts and the first due date. */
const isOeFirstIncomplete = (delivery: InvoiceDeliveryItemDto): boolean => {
  if (delivery.invoiceTemplate !== InvoiceTemplate.OE_FIRST) return false
  const meta = (delivery.invoiceMetadata as InvoiceMetadata | null) ?? {}
  return (
    (meta.totalDue ?? 0) <= 0 ||
    (meta.balanceDue ?? 0) <= 0 ||
    isNil(delivery.dueDate)
  )
}

/**
 * Why the send action is unavailable, or null when it is available. The row's
 * Send also checks this; bulk send leaves an incomplete OE1 selectable so the
 * review lists it with this reason.
 */
export function resolveSendDisabledReason(
  delivery: InvoiceDeliveryItemDto
): string | null {
  if (!delivery.clientId)
    return 'Match this delivery to a company before sending'
  if (delivery.hasBillingProfile === false)
    return 'This company is deactivated or has no email recipients configured'
  if (delivery.status === InvoiceDeliveryStatus.Sending)
    return 'Delivery is currently being sent'
  if (delivery.isArchived) return 'Unarchive this delivery before sending'
  if (!delivery.isLatest) return 'Only the latest version can be sent'
  if (isOeFirstIncomplete(delivery))
    return 'Add both amounts and a due date to this OE - Initial Funding invoice before sending'
  return null
}

/**
 * ExcelJS object cells (formula/rich-text) can stringify to "[object Object]".
 * Never surface that in a warning message; return '' when there's no meaningful
 * raw value to show.
 */
export const cleanParseWarningRawValue = (
  rawValue: string | null | undefined
): string => {
  if (isNil(rawValue)) return ''
  const trimmed = rawValue.trim()
  return trimmed === '[object Object]' ? '' : trimmed
}

export function triggerFileDownload(
  e: React.MouseEvent,
  url: string,
  fileName: string
): void {
  e.preventDefault()
  e.stopPropagation()
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/**
 * Formats a byte count for display during a download, e.g. "14.2 MB".
 *
 * Uses MB/GB rather than MiB/GiB to match what the OS download UI shows, so the
 * two don't appear to disagree.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1000
  if (kb < 1000) return `${Math.round(kb)} KB`
  const mb = kb / 1000
  if (mb < 1000) return `${mb.toFixed(1)} MB`
  return `${(mb / 1000).toFixed(2)} GB`
}

export const roundCents = (amount: number): number =>
  Math.round(amount * 100) / 100

/** An edit wins over the fallback; `undefined` means untouched, `null` means cleared. */
export const pickEdit = <T>(edit: T | undefined, fallback: T): T =>
  edit === undefined ? fallback : edit

/** InputNumber formatter: "8,420.00" at rest, the raw text while typing. */
export const formatAmountInput = (
  value: string | number | undefined,
  info: { userTyping: boolean; input: string }
): string => {
  if (info.userTyping) return info.input
  const amount = Number(value)
  return isNil(value) || value === '' || !Number.isFinite(amount)
    ? ''
    : amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
}

export const parseAmountInput = (value: string | undefined): string =>
  (value ?? '').replace(/,/g, '')

/** Wire format for the due date, matching what EPS accepts and returns. */
/** Invoice list page sizes; the list is loaded whole, so these only change how much shows. */
export const DELIVERY_PAGE_SIZE_OPTIONS = [25, 50, 100, 200]
/** Opens big enough for a whole month to be selected and sent at once. */
export const DELIVERY_DEFAULT_PAGE_SIZE = 200

export const DUE_DATE_WIRE_FORMAT = 'YYYY-MM-DD'

/** Date pickers show "Dec 15, 2026" but also accept a typed "12/15/2026". */
export const DATE_INPUT_FORMATS = ['MMM DD, YYYY', 'MM/DD/YYYY', 'M/D/YYYY']
export const DATE_INPUT_PLACEHOLDER = 'MM/DD/YYYY'

/**
 * Formats a `YYYY-MM-DD` due date for display, null when there is none.
 * Parsed with dayjs because `new Date` reads a bare date as UTC and shifts it
 * a day earlier in western timezones.
 */
export function formatDueDate(
  dueDate: string | null | undefined
): string | null {
  if (isNil(dueDate)) return null
  const parsed = dayjs(dueDate)
  return parsed.isValid() ? parsed.format('MMM DD, YYYY') : null
}

export function formatCompactDate(dateStr: string): string {
  const d = new Date(dateStr)
  const date = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const time = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  })
  return `${date} · ${time}`
}
